// La tarjeta de Estado de la ficha de un producto — ADR-2026-10-06-la-ficha-ocupa-la-pantalla.
//
// Antes el estado era un globito "Listo" junto al nombre y, si faltaba algo, un cartel
// con la lista de faltantes que no llevaba a ningún lado: había que buscar a mano en qué
// sección estaba el precio. Ahora es la tarjeta de arriba de la columna derecha (como el
// "Estado" de la ficha de Shopify): dice si el producto está listo, qué le falta —cada
// cosa es un atajo a su campo— y tiene las dos acciones que no son de ninguna sección.
import { AlertCircle, Check, ChevronRight, Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { boton } from './botones';
import { estadoDeFila } from './estadoDeFila';
import { irASeccion } from './Seccion';
import { nombresDeDestinos, type CanalLanzamiento } from './canales';
import type { ProblemaDeFicha } from './fichaTiendanube';
import type { ProductoDeLanzamiento } from './lanzamientosApi';
import { yaPublicadoEnMl } from './publicarLanzamiento';
import { faltaParaMl } from '../../lib/mercadoLibrePublicarLogica';

const alPrincipio = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** En qué sección de la ficha se arregla cada problema. `undefined` = en ninguna a la vista. */
export function seccionDelProblema(campo: string): string | undefined {
  if (campo === 'nombre') return 'basico';
  if (campo === 'precio') return 'precio';
  if (campo === 'sku') return 'inventario';
  if (campo === 'envio') return 'envio';
  if (campo === 'categorias') return 'categorias';
  if (campo.startsWith('seo')) return 'seo';
  if (campo.startsWith('variante') || campo === 'atributos') return 'variantes';
  return undefined;
}

interface Pendiente {
  texto: string;
  seccion?: string;
}

/**
 * Lo que le falta para Mercado Libre, con atajo a su sección (el stock, a Inventario). null = nada, o
 * no va a Mercado Libre. Es el mismo chequeo que frena el botón Publicar (prueba en vivo del 16/10:
 * la tarjeta decía "Listo para publicar" con la sección de Mercado Libre pidiendo la categoría).
 */
export function pendienteDeMl(producto: ProductoDeLanzamiento, destinos: readonly CanalLanzamiento[], zip: boolean): Pendiente | null {
  if (zip || !destinos.includes('mercadolibre')) return null;
  const falta = faltaParaMl(producto, producto.mercadolibre);
  if (!falta) return null;
  return { texto: alPrincipio(falta), seccion: falta === 'falta el stock' ? 'inventario' : 'mercadolibre' };
}

export function TarjetaDeEstado({
  producto,
  fotos,
  seCrea,
  zip,
  destinos,
  problemas,
  guardando,
  comentariosPendientes,
  fotosEnVariantes = false,
  onCompletarConIa,
  onAbrirComentarios,
}: {
  producto: ProductoDeLanzamiento;
  /** Las fotos en pantalla (la ficha las reordena y quita antes de guardar). */
  fotos: number;
  seCrea: boolean;
  zip: boolean;
  destinos: readonly CanalLanzamiento[];
  problemas: readonly ProblemaDeFicha[];
  guardando: boolean;
  comentariosPendientes: number;
  /** Las fotos se cargan en cada variante: no hay sección Fotos a la que llevar. */
  fotosEnVariantes?: boolean;
  onCompletarConIa: () => void;
  onAbrirComentarios: () => void;
}) {
  const estado = estadoDeFila(producto, destinos, zip ? 'descargar' : 'publicar');

  // Lo que falta, entero y en orden: primero lo que impide publicar sin discusión (sin
  // producto, sin fotos), después lo de la ficha.
  const pendientes: Pendiente[] = [];
  if (!producto.productoId) pendientes.push({ texto: 'Atalo a un producto del catálogo' });
  // Con fotos por variante no hay sección Fotos: se suman en cada variante (review #168).
  if (fotos === 0) pendientes.push({ texto: 'Sumá al menos una foto', seccion: fotosEnVariantes ? 'variantes' : 'fotos' });
  for (const p of problemas) pendientes.push({ texto: alPrincipio(p.mensaje), seccion: seccionDelProblema(p.campo) });
  // Lo de Mercado Libre, sin repetir lo que ya dijo la ficha (el stock puede venir de los dos lados).
  const deMl = pendienteDeMl(producto, destinos, zip);
  if (deMl && !pendientes.some((x) => x.texto.toLowerCase() === deMl.texto.toLowerCase())) pendientes.push(deMl);

  // Sólo Mercado Libre: "ya en la tienda" / "se va a crear" son de Tiendanube y no aplican
  // (traído de principal al mergear, f25f4fe7).
  const dondeVa = zip
    ? 'Sus fotos van a una carpeta propia dentro del ZIP.'
    : destinos.length > 0 && destinos.every((d) => d === 'mercadolibre')
      ? yaPublicadoEnMl(producto)
        ? 'Ya está en Mercado Libre: publicarlo de nuevo no lo duplica.'
        : 'Se va a publicar en Mercado Libre.'
      : seCrea
      ? `Se crea como producto nuevo en ${nombresDeDestinos(destinos)}.`
      : 'Ya existe en tu tienda: se le suman estas fotos y su ficha no se toca.';

  const yaSalio = estado.tono === 'publicado' || estado.tono === 'publicando' || estado.tono === 'error';

  return (
    <section
      data-tour="ficha-estado"
      aria-label="Estado del producto"
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 py-3 px-4 sm:px-5">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Estado</p>
        {guardando && (
          <span className="flex items-center gap-1.5 text-xs text-neutral-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando
          </span>
        )}
      </div>

      <div className="space-y-3 p-4 sm:p-5">
        {yaSalio ? (
          <div
            className={`flex items-start gap-3 rounded-xl p-3 ${
              estado.tono === 'error' ? 'bg-red-50 text-red-700' : estado.tono === 'publicando' ? 'bg-sky-50 text-sky-700' : 'bg-[#EEF2FF] text-[#1644EB]'
            }`}
          >
            {estado.tono === 'publicando' ? (
              <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
            ) : estado.tono === 'error' ? (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
            )}
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{estado.texto}</span>
              {estado.detalle && <span className="mt-0.5 block text-xs">{estado.detalle}</span>}
            </span>
          </div>
        ) : pendientes.length === 0 ? (
          <div className="flex items-start gap-3 rounded-xl bg-emerald-50 p-3 text-emerald-800">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{estado.texto}</span>
              <span className="mt-0.5 block text-xs text-emerald-700">{dondeVa}</span>
            </span>
          </div>
        ) : (
          <div className="rounded-xl bg-amber-50 p-3 ring-1 ring-inset ring-amber-600/15">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
              {pendientes.length === 1 ? 'Le falta 1 dato para publicar' : `Le faltan ${pendientes.length} datos para publicar`}
            </p>
            {/* Cada faltante lleva a su campo: abre la sección si estaba plegada. */}
            <ul className="mt-2 space-y-1">
              {pendientes.map((p, i) => (
                <li key={i}>
                  {p.seccion ? (
                    <button
                      type="button"
                      onClick={() => irASeccion(p.seccion!)}
                      className="group flex w-full items-start gap-2 rounded-lg py-1.5 px-2 text-left text-xs text-amber-900 transition-colors hover:bg-amber-100/70"
                    >
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                      <span className="min-w-0 flex-1">{p.texto}</span>
                      <ChevronRight className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <span className="flex items-start gap-2 py-1.5 px-2 text-xs text-amber-900">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                      {p.texto}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!yaSalio && pendientes.length > 0 && <p className="text-xs text-neutral-500">{dondeVa}</p>}
        {producto.error && estado.tono !== 'error' && (
          <p className="rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{producto.error}</p>
        )}

        {/* Completar con IA vive acá y no adentro de una sección: propone datos de VARIAS
            a la vez, así que no es de ninguna. Solo al crear: a un producto que ya está en
            la tienda no se le toca la ficha. Comentarios va SIN esa condición: sobre un
            producto que ya está en la tienda también hay cosas que decir. */}
        <div className="flex flex-col gap-2">
          {seCrea && (
            <button
              type="button"
              onClick={onCompletarConIa}
              data-tour="ficha-ia"
              className={boton('secundario', { extra: 'w-full' })}
            >
              <Sparkles className="h-4 w-4" />
              Completar con IA
            </button>
          )}
          <button type="button" onClick={onAbrirComentarios} className={boton('neutro', { extra: 'w-full' })}>
            <MessageSquare className="h-4 w-4 text-neutral-500" />
            Comentarios
            {comentariosPendientes > 0 && (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand-primary px-1.5 text-[11px] font-semibold text-white">
                {comentariosPendientes}
              </span>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
